import "./SearchResultPageContent.scss";

import PropTypes from "prop-types";

import * as React from "react";
import { Col, Row } from "react-bootstrap";
import * as articleUtils from "../../../../utils/articleUtils";
import { ArticleTrackContainer } from "../../../../utils/ArticleTrackingUtil";
import * as bundleSelectionUtil from "../../../../utils/BundleSelectionUtil";
import { getSearchResultRows, DEFAULT_ROWS_LIST } from "../../../../utils/SearchUtil";
import LoadMoreButton from "../../../general/LoadMoreButton";
import LoadingPanel from "../../../general/LoadingPanel";
import SearchResultNoArticle from "./SearchResultNoArticle";

import { SearchResultArticle } from "./SearchResultArticle";

import SEARCH_RESULT_LAYOUT from "./SearchResultLayout";

import {
    SearchResultItem,
    SearchResultDebugInfo,
    SearchResultLtrDebugInfo,
    SearchResultEx
} from "shared/src/types/articlesearch/SearchResult";

import { PageType } from "shared/src/types/PageType";
import { ReplacementKind } from "shared/src/tracking/Tracking";
import { isShowCSatSurveyActive } from "../../../../searchdiscover/toggles";
import { SessionStore as OriginalSessionStore } from "shared/src/uidispatcherapi/SessionStore";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import Tracking from "shared/src/uidispatcherapi/Tracking";
import { SearchResultRenderData } from "shared/src/types/RenderData";

/*
 * The list of search results.
 */
type Props = {
    query?: string;
    fullPageBanner?: any;
    articleTeaserBanner?: any;
    replace?: {
        [x: string]: any;
    };
    listOfArticles?: Array<any>;
    results?: SearchResultEx | null;
    showLoadMoreButton?: boolean;
    searchRunning?: boolean;
    loadingMessageId: string;
    customerStore?: string;
    loadMoreResultsHandler: Function;
    pageType: PageType;
    replacementKind?: ReplacementKind;
};

class SearchResultPageContent extends React.Component<Props> {
    static propTypes = {
        query: PropTypes.string,
        fullPageBanner: PropTypes.node,
        articleTeaserBanner: PropTypes.node,

        replace: PropTypes.object,
        listOfArticles: PropTypes.array,

        results: PropTypes.object,
        showLoadMoreButton: PropTypes.bool,

        searchRunning: PropTypes.bool,
        loadingMessageId: PropTypes.string.isRequired,

        customerStore: PropTypes.string,

        // handlers
        loadMoreResultsHandler: PropTypes.func.isRequired
    };
    static contextTypes = {
        intl: PropTypes.object
    };

    articleUtilToTrackArticlesForGTM = new ArticleTrackContainer();

    componentDidMount() {
        this.trackArticleIdsForGTM();
    }

    componentDidUpdate() {
        this.trackArticleIdsForGTM();
    }

    getBettyBundleIdForArticle = (article: { [x: string]: any }): string | undefined | null => {
        if (article.variant && article.variant.bundles) {
            return bundleSelectionUtil.chooseBundleToShow(article.variant.bundles, this.props.customerStore);
        }
        return null;
    };

    trackArticleIdsForGTM() {
        if (this.props.results && this.props.results.result) {
            const selectedBundles = this.props.results.result.map(this.getBettyBundleIdForArticle);
            this.articleUtilToTrackArticlesForGTM.trackArticles(selectedBundles);
        }
    }

    getResults() {
        const results = this.props.results;

        const renderedResults: Array<React.ReactNode> =
            results && results.result
                ? results.result.map(this.variantToSearchResultArticle.bind(this, false, "r"))
                : [];
        const numbersLoaded = results && results.resultIds ? results.resultIds.length : 0;
        const amountTotal = results ? results.amount : 0;
        const allCurrentResultsLoaded =
            renderedResults.length === amountTotal || renderedResults.length === numbersLoaded;

        const rows = results ? results.rows : 0;

        if (isShowCSatSurveyActive()) {
            const csatUrl =
                CountryUtil.getOptionalConfig()?.articlesearchCsatUrl ??
                "https://feedback.metro-cc.com/jfe/form/SV_cvfNGQ1ryE1HahE";
            const performanceCookiesAccepted = OriginalSessionStore.isCookieCategoryAllowed("performance");
            if (performanceCookiesAccepted) {
                const defaultCSatPosition = 6;
                const csatPosition = Math.min(renderedResults.length, defaultCSatPosition) || renderedResults.length;
                const csatElement = (
                    <span>
                        <div id={"articlesearch-csat"} key={"articlesearch-csat"} className={"articlesearch-csat"}>
                            <iframe
                                className={"articlesearch-csat-iframe"}
                                title={this.context.intl.formatMessage({ id: "SD_CSAT_TITLE" })}
                                src={
                                    csatUrl +
                                    "?SearchQuery=" +
                                    this.props.results?.query +
                                    "&SubContextId=" +
                                    Tracking.getCurrentSubContextId()
                                }
                            />
                        </div>
                    </span>
                );
                renderedResults.splice(csatPosition, 0, csatElement);
            }
        }

        return {
            renderedResults: renderedResults,
            numbersLoaded: numbersLoaded,
            amount: amountTotal,
            rows: rows,
            allCurrentResultsLoaded: allCurrentResultsLoaded
        };
    }

    itemFromVariant(
        variant:
            | {
                  [x: string]: any;
              }
            | undefined
            | null,
        position: number | undefined | null,
        type: "SearchResult" | "FavouriteResult" | void,
        debugInfo: SearchResultDebugInfo | undefined | null,
        ltrDebugInfo?: SearchResultLtrDebugInfo | undefined | null,
        ropoRank?: number | undefined | null
    ): SearchResultRenderData | null {
        if (!variant) {
            return null;
        }
        return {
            name: variant.description,
            url: variant.imageUrl,
            variant: variant,
            variantId: variant.variantId,
            bundles: articleUtils.bettyBundleIdInsteadOfBundleNumber(variant.bundles, variant.bundles),
            selectedBundle: bundleSelectionUtil.chooseBundleToShow(
                variant.bundles,
                this.props.customerStore,
                undefined,
                this.props.query
            ),
            bundleSelector: articleUtils.bettyBundleIdInsteadOfBundleNumber(variant.bundles, variant.bundleSelector),
            position: position,
            type: type,
            debugInfo: debugInfo,
            ltrDebugInfo: ltrDebugInfo,
            ropoRank: ropoRank
        };
    }

    variantToSearchResultArticle = (
        inContainer: boolean,
        id: string,
        result: SearchResultItem,
        index: number
    ): React.ReactNode => {
        if (result.state === "missing" || result.state === "error") {
            return (
                <SearchResultNoArticle
                    key={result.id}
                    inContainer={inContainer}
                    item={result.id}
                    searchResultLayout={SEARCH_RESULT_LAYOUT.GRID}
                    __bypassReactAdapter
                />
            );
        }
        if (result.state === "requested" || result.state === "loading") {
            return null;
        }
        const variantItem = this.itemFromVariant(
            result.variant,
            result.position,
            result.type,
            result.debugInfo,
            result.ltrDebugInfo,
            result.ropoRank
        );
        const successorItem = this.itemFromVariant(
            result.successor,
            result.position,
            result.type,
            null,
            null,
            result.ropoRank
        );

        if (variantItem === null) {
            const noResult = (
                <SearchResultNoArticle
                    key={result.id}
                    inContainer={inContainer}
                    item={result.id}
                    searchResultLayout={SEARCH_RESULT_LAYOUT.GRID}
                    __bypassReactAdapter
                />
            );
            return [noResult];
        }

        let deprecatedArticle = null;
        const reactId = (result.type || "") + result.id + id + index;
        const isIdQuery = this.props.query && this.props.query.trim().match(/^[0-9]+$/); // id queries are numerical
        if (successorItem !== null) {
            if (isIdQuery) {
                deprecatedArticle = (
                    <SearchResultArticle
                        key={"successor" + reactId}
                        inContainer={inContainer}
                        item={variantItem}
                        listOfArticles={this.props.listOfArticles}
                        deprecated
                        searchResultLayout={SEARCH_RESULT_LAYOUT.GRID}
                        pageType={this.props.pageType}
                        query={this.props.query}
                        replacementKind={this.props.replacementKind}
                        solrScore={result.solrScore}
                    />
                );
            }
            // the successor is the actual article (it replaces the now deprecated article)
            return (
                <SearchResultArticle
                    key={reactId}
                    inContainer={inContainer}
                    item={successorItem}
                    listOfArticles={this.props.listOfArticles}
                    replace={this.props.replace}
                    onList={result.onList}
                    recentlyOrdered={result.recentlyOrdered}
                    searchResultLayout={SEARCH_RESULT_LAYOUT.GRID}
                    pageType={this.props.pageType}
                    renderedDeprecatedArticle={deprecatedArticle}
                    deprecated={false}
                    query={this.props.query}
                    replacementKind={this.props.replacementKind}
                    solrScore={result.solrScore}
                />
            );
        } else {
            return (
                <SearchResultArticle
                    key={reactId}
                    inContainer={inContainer}
                    item={variantItem}
                    listOfArticles={this.props.listOfArticles}
                    replace={this.props.replace}
                    onList={result.onList}
                    recentlyOrdered={result.recentlyOrdered}
                    searchResultLayout={SEARCH_RESULT_LAYOUT.GRID}
                    pageType={this.props.pageType}
                    deprecated={false}
                    query={this.props.query}
                    replacementKind={this.props.replacementKind}
                    solrScore={result.solrScore}
                />
            );
        }
    };

    renderLoadMoreButton = (results: { [x: string]: any }) => {
        if (!this.props.showLoadMoreButton) {
            return null;
        }
        const numberOfNextLoadResults = Math.min(
            results.amount - results.numbersLoaded,
            this.props.pageType === "STORE_LIST" ? DEFAULT_ROWS_LIST : getSearchResultRows()
        );
        return (
            <LoadMoreButton
                key="loadMore"
                inContainer={false}
                loadmore={this.props.loadMoreResultsHandler}
                loaded={results.numbersLoaded}
                loadnext={numberOfNextLoadResults}
                amount={results.amount}
            />
        );
    };

    renderLoadingPanel = (results: { [x: string]: any }) => {
        const searchRunning = this.props.searchRunning;
        if (searchRunning || !results.allCurrentResultsLoaded) {
            return <LoadingPanel messageId={this.props.loadingMessageId} />;
        }
        return null;
    };

    render() {
        const results = this.getResults();
        const loadMoreButton = this.renderLoadMoreButton(results);
        const loadingPanel = this.renderLoadingPanel(results);

        const layoutClassName = "mfcss_card-article-2--grid-container-flex";

        return (
            <Col lg={9}>
                {this.props.fullPageBanner}
                {this.props.articleTeaserBanner}
                <div className={layoutClassName}>{results.renderedResults}</div>
                <Row>
                    <Col xs={12}>{loadMoreButton}</Col>
                </Row>
                {loadingPanel}
            </Col>
        );
    }
}

export default SearchResultPageContent;
